// Pure date-range helpers for the Hours reporting page (Phase 2a.5).
// No I/O; unit-tested in hours-range.test.ts.
//
// ⚠️ All formatting uses LOCAL date parts (getFullYear/getMonth/getDate), NOT
// toISOString().slice() — UTC slicing drifts to tomorrow after ~5pm ET for a
// US contractor (feedback_today_is_client_local_date). "Today" must be the
// office's local calendar date.

/** Format a Date as YYYY-MM-DD using LOCAL parts (zero-padded). */
export function localISODate(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Current week range: Monday of the current week → today (LOCAL).
 * Thin wrapper over weekRangeFor with the historical Monday default — kept for
 * the zero-arg callers (HoursPage mount default + AppShell prefetch warm key).
 */
export function currentWeekRange(): { from: string; to: string } {
  const today = localISODate();
  return weekRangeFor(today, 'mon', today);
}

/** This-month range: 1st of the current month → today (LOCAL). */
export function monthRange(): { from: string; to: string } {
  const today = localISODate();
  return monthRangeFor(today.slice(0, 7), today);
}

/** Current-month range: 1st of the current month → today (LOCAL). Alias of
 *  monthRange under the Crew Utilization naming (paired with currentWeekRange);
 *  drives the month-scoped KPI tiles + per-person Month column. */
export function currentMonthRange(): { from: string; to: string } {
  return monthRange();
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** True when both are YYYY-MM-DD and from <= to (ISO strings sort chronologically). */
export function isValidRange(from: string, to: string): boolean {
  return DATE_RE.test(from) && DATE_RE.test(to) && from <= to;
}

// ===========================================================================
// Navigable / configurable ranges (Crew Utilization month-picker + week-start)
//
// The Hours page lets the office pick any month and any week, and choose which
// weekday the (pay-period) week starts on. All math stays on LOCAL date parts
// per the UTC-drift warning at the top of this file. ISO 'YYYY-MM-DD' /
// 'YYYY-MM' strings sort lexicographically === chronologically, so plain string
// compare is used for min/clamp throughout.
// ===========================================================================

/** Weekday codes indexed to match JS getDay() (0=Sun .. 6=Sat). */
export const WEEKDAY_CODES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
export type WeekStartCode = (typeof WEEKDAY_CODES)[number];

export const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;
const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/** Narrowing guard for a URL/string week-start value. */
export function isWeekStartCode(s: string | null | undefined): s is WeekStartCode {
  return !!s && (WEEKDAY_CODES as readonly string[]).includes(s);
}

/** Parse a 'YYYY-MM-DD' string into a LOCAL Date (no UTC). */
function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Add days to a 'YYYY-MM-DD' string, returning 'YYYY-MM-DD' (LOCAL). */
export function addDaysIso(iso: string, delta: number): string {
  const d = parseLocalDate(iso);
  d.setDate(d.getDate() + delta);
  return localISODate(d);
}

/**
 * Pure month arithmetic on a 'YYYY-MM-01' string (no Date — avoids tz drift).
 * Canonical copy; FinancialsPage imports this rather than keeping its own.
 */
export function addMonthsIso(iso: string, delta: number): string {
  const [y, m] = iso.split('-').map(Number);
  const total = y * 12 + (m - 1) + delta;
  const ny = Math.floor(total / 12);
  const nm = total % 12;
  return `${ny}-${String(nm + 1).padStart(2, '0')}-01`;
}

/**
 * Start-of-week date (LOCAL) for the given anchor + week-start day.
 * offset = (anchorDay - startIdx + 7) % 7 — generalizes the old Monday `(day+6)%7`.
 * IDEMPOTENT: snapping an already-on-boundary date returns it unchanged, which the
 * URL-sync effect relies on. `snapWeekStart` is an explicit alias for that intent.
 */
export function startOfWeek(anchorISO: string, weekStart: WeekStartCode): string {
  const startIdx = WEEKDAY_CODES.indexOf(weekStart);
  const offset = (parseLocalDate(anchorISO).getDay() - startIdx + 7) % 7;
  return addDaysIso(anchorISO, -offset);
}
export const snapWeekStart = startOfWeek;

/**
 * Week range for an anchor + week-start: from = start-of-week, to = min(start+6, today).
 * Past week → full 7 days; current week → week-to-date (preserves prior behavior).
 */
export function weekRangeFor(
  anchorISO: string,
  weekStart: WeekStartCode,
  todayISO: string,
): { from: string; to: string } {
  const from = startOfWeek(anchorISO, weekStart);
  const fullEnd = addDaysIso(from, 6);
  return { from, to: fullEnd > todayISO ? todayISO : fullEnd };
}

/** Last calendar day ('YYYY-MM-DD') of the month containing monthISO ('YYYY-MM'[-DD]). */
export function lastDayOfMonthIso(monthISO: string): string {
  const firstNext = addMonthsIso(`${monthISO.slice(0, 7)}-01`, 1);
  return addDaysIso(firstNext, -1);
}

/**
 * Month range for a 'YYYY-MM': from = 1st, to = min(last day, today).
 * Past month → full month; current month → month-to-date (no future workdays
 * inflating throughput / workdaysBetween denominators).
 */
export function monthRangeFor(
  monthISO: string,
  todayISO: string,
): { from: string; to: string } {
  const from = `${monthISO.slice(0, 7)}-01`;
  const lastDay = lastDayOfMonthIso(monthISO);
  return { from, to: lastDay > todayISO ? todayISO : lastDay };
}

// --- Clamp-on-read guards (typed/bookmarked URLs can hold garbage/future) -----

/** Validate a week-start code, falling back to 'mon'. */
export function clampWeekStart(s: string | null | undefined): WeekStartCode {
  return isWeekStartCode(s) ? s : 'mon';
}

/**
 * Normalize a week anchor to a valid start-of-week date that is NOT in the future.
 * Handles malformed input and clamps a future week back to the current one. The
 * result is already snapped, so re-running is idempotent.
 */
export function clampWeekAnchor(
  anchorISO: string | null | undefined,
  weekStart: WeekStartCode,
  todayISO: string,
): string {
  const curStart = startOfWeek(todayISO, weekStart);
  if (!anchorISO || !DATE_RE.test(anchorISO)) return curStart;
  const start = startOfWeek(anchorISO, weekStart);
  return start > curStart ? curStart : start;
}

/** Normalize a month to 'YYYY-MM', clamped to the current month (no future, no garbage). */
export function clampMonth(
  monthISO: string | null | undefined,
  todayISO: string,
): string {
  const cur = todayISO.slice(0, 7);
  if (!monthISO) return cur;
  const m = monthISO.slice(0, 7);
  if (!MONTH_RE.test(m)) return cur;
  return m > cur ? cur : m;
}

// --- Navigation + disable-future predicates ---------------------------------

export function prevWeek(weekStartISO: string): string {
  return addDaysIso(weekStartISO, -7);
}
export function nextWeek(weekStartISO: string): string {
  return addDaysIso(weekStartISO, 7);
}
export function prevMonth(monthISO: string): string {
  return addMonthsIso(`${monthISO.slice(0, 7)}-01`, -1).slice(0, 7);
}
export function nextMonth(monthISO: string): string {
  return addMonthsIso(`${monthISO.slice(0, 7)}-01`, 1).slice(0, 7);
}

/** True when the shown week is the current week or later (→ disable "next"). */
export function isCurrentOrLaterWeek(
  weekStartISO: string,
  weekStart: WeekStartCode,
  todayISO: string,
): boolean {
  return weekStartISO >= startOfWeek(todayISO, weekStart);
}
/** True when the shown month is the current month or later (→ disable "next"). */
export function isCurrentOrLaterMonth(monthISO: string, todayISO: string): boolean {
  return monthISO.slice(0, 7) >= todayISO.slice(0, 7);
}

// --- Human labels -----------------------------------------------------------

/** "June 2026" from a 'YYYY-MM'. */
export function formatMonthLabel(monthISO: string): string {
  const [y, m] = monthISO.slice(0, 7).split('-').map(Number);
  return `${MONTHS_FULL[m - 1]} ${y}`;
}

/** "Jun 3 – Jun 9" (or "May 31 – Jun 6" across a month edge) from a from/to range. */
export function formatWeekLabel(from: string, to: string): string {
  const f = parseLocalDate(from);
  const t = parseLocalDate(to);
  const fl = `${MONTHS_SHORT[f.getMonth()]} ${f.getDate()}`;
  const tl = `${MONTHS_SHORT[t.getMonth()]} ${t.getDate()}`;
  return `${fl} – ${tl}`;
}
